import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core'
import {
  AdvancedFilters,
  DEFAULT_KANBAN_FILTERS,
  FilterValue,
  FIXED_CARD_FIELD_IDS,
  filterKey,
  KanbanDateFilter,
  KanbanFilter,
  KanbanFiltersChange,
  KanbanTextFilter,
  Operator,
  OPERATOR_SYMBOLS,
} from './kanban-filters.model'
import { VisioComponentsModule } from '@visio-ai/ui-kit'
import { FormsModule } from '@angular/forms'
import { Kanban } from '../../../../_services/kanban/kanban.model'
import { KanbanFiltersPanelComponent } from './kanban-filters-panel/kanban-filters-panel.component'
import { KanbanPropertyFilterComponent } from './kanban-property-filter/kanban-property-filter.component'
import { Location } from '@angular/common'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { ActivatedRoute, Router } from '@angular/router'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { PvtUsersService } from '../../../../_services/users/users.service'
import { KanbanUsersByRoleService, RoleUser } from '../../services/kanban-users-by-role.service'
import { getCurrentUserId } from '../../utils/current-user'
import {
  buildFilterEntries,
  isDateRangeValue,
  parseAdvancedQueryParam,
  parseStandardQueryParam,
  resolveInitialValue,
  serializeFilterValue,
} from './kanban-filters.utils'

dayjs.extend(customParseFormat)

const REVIEWER_ROLE_IDS = ['visio_reviewer', 'visio_reviewer_r2', 'visio_superviewer']
const CS_ROLE_IDS = ['visio_pm', 'visio_cs']

@Component({
  selector: 'app-kanban-filters',
  imports: [
    VisioComponentsModule,
    FormsModule,
    KanbanFiltersPanelComponent,
    KanbanPropertyFilterComponent
  ],
  templateUrl: './kanban-filters.component.html',
  styleUrl: './kanban-filters.component.scss'
})
export class KanbanFiltersComponent {
  private elementRef = inject(ElementRef)
  private destroyRef = inject(DestroyRef)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)
  private location = inject(Location)
  private usersService = inject(PvtUsersService)
  private usersByRoleService = inject(KanbanUsersByRoleService)

  filters = input<KanbanFilter[]>([])
  boardFields = input<Kanban.Board.Field[]>([])
  tags = input<Kanban.Board.Tag[]>([])
  phases = input<Kanban.Board.Phase[]>([])
  userRoleMap = input<Record<string, 'reviewer' | 'cs'>>({ userInCharge: 'cs' })
  showAdvancedFilters = input(true)

  @HostListener('document:click', ['$event.target'])
  protected onDocumentClick(target: EventTarget | null): void {
    if (this.showAdvancedPanel() && !this.elementRef.nativeElement.contains(target)) {
      this.showAdvancedPanel.set(false)
    }
  }

  private advancedBtnRef = viewChild('advancedBtn', { read: ElementRef })

  selectedValues = signal<Record<string, FilterValue>>({})
  advancedFilters = signal<AdvancedFilters>({})
  showAdvancedPanel = signal(false)
  protected panelPosition = signal<{ top: number; left: number }>({ top: 0, left: 0 })
  private shopOptions = signal<{ label: string; value: string }[]>([])
  protected userOptions = computed(() =>
    [...this.reviewerUsers(), ...this.csUsers()]
      .map(u => ({ value: u.id, label: u.name }))
      .sort((a, b) => (a.label < b.label ? -1 : 1))
  )
  private optionsByKey = computed(() =>
    new Map<string, { label: string; value: string }[]>([
      ['shopId', this.shopOptions()],
      ['user', this.userOptions()],
      ['tagId', this.tagOptions()],
    ])
  )
  private reviewerUsers = signal<RoleUser[]>([])
  private csUsers = signal<RoleUser[]>([])
  protected reviewerUserOptions = computed(() =>
    this.reviewerUsers()
      .map(u => ({ value: u.id, label: u.name }))
      .sort((a, b) => (a.label < b.label ? -1 : 1))
  )
  private csUserOptions = computed(() =>
    this.csUsers()
      .map(u => ({ value: u.id, label: u.name }))
      .sort((a, b) => (a.label < b.label ? -1 : 1))
  )
  protected usersByField = computed(() => {
    const roleMap = this.userRoleMap()
    const reviewerOpts = this.reviewerUserOptions()
    const csOpts = this.csUserOptions()
    return Object.fromEntries(
      this.boardFields()
        .filter(f => f.type === 'user')
        .map(f => [f.id, roleMap[f.id] === 'cs' ? csOpts : reviewerOpts])
    )
  })
  private currentUserId = getCurrentUserId()

  onFiltersChange = output<KanbanFiltersChange>()
  onSelectedValuesChange = output<Record<string, string>>()

  protected readonly filterKey = filterKey

  protected mergedFilters = computed(() => {
    const extra = this.filters()
    const extraByKey = new Map(extra.map(f => [filterKey(f), f]))
    const defaultKeys = new Set(DEFAULT_KANBAN_FILTERS.map(f => filterKey(f)))

    const merged = DEFAULT_KANBAN_FILTERS.map(f => extraByKey.get(filterKey(f)) ?? f)
    extra.filter(f => !defaultKeys.has(filterKey(f))).forEach(f => merged.push(f))

    return merged
  })

  private tagOptions = computed(() => this.tags().map(t => ({ label: t.label, value: t.id })))

  protected displayFilters = computed(() => {
    const opts = this.optionsByKey()
    return this.mergedFilters().map(f => {
      if (f.type === 'select') {
        const options = opts.get(f.field.id) ?? opts.get(filterKey(f))
        if (options) return { ...f, options }
      }
      if (f.type === 'check' && filterKey(f) === 'user')
        return { ...f, value: this.currentUserId }
      return f
    })
  })

  protected advancedFilterChips = computed(() =>
    Object.entries(this.advancedFilters())
      .filter(([, v]) => v.value)
      .map(([fieldId, { operator, value, rangeEnd }]) => {
        const field = this.boardFields().find(f => f.id === fieldId)
        const displayValue = rangeEnd
          ? `${value} — ${rangeEnd}`
          : `${OPERATOR_SYMBOLS[operator]} ${value}`
        const label = field ? `${field.label} ${displayValue}` : fieldId
        return { fieldId, label }
      })
  )

  constructor() {
    this.usersService
      .getUserEstabelecimentos('me')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ estabelecimentos }) => {
        this.shopOptions.set(estabelecimentos.map(e => ({ label: e.nome, value: e._id })))
      })

    this.usersByRoleService
      .getUsersByRoles(REVIEWER_ROLE_IDS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(users => this.reviewerUsers.set(users))

    this.usersByRoleService
      .getUsersByRoles(CS_ROLE_IDS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(users => this.csUsers.set(users))

    let isInitializing = false

    effect(() => {
      const filters = this.mergedFilters()
      const queryParams = this.readQueryParams()

      const initial = this.resolveInitialValues(filters)
      this.applyPreSelectedValues(initial, queryParams)
      this.enforceMutualExclusivity(initial, filters, queryParams)

      const initialAdvanced = this.readAdvancedFiltersFromQueryParams(queryParams)
      const hasDefaults = Object.values(initial).some(v => v !== null)
      const hasAdvancedDefaults = Object.values(initialAdvanced).some(v => !!v.value)
      isInitializing = !hasDefaults && !hasAdvancedDefaults
      this.selectedValues.set(initial)
      this.advancedFilters.set(initialAdvanced)
    })

    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    effect(() => {
      const selectedValues = this.selectedValues()
      const advancedFilters = this.advancedFilters()

      if (isInitializing) {
        isInitializing = false
        return
      }

      if (debounceTimer) clearTimeout(debounceTimer)

      debounceTimer = setTimeout(() => {
        const serialized = this.serializeSelectedValues(selectedValues)
        const serializedAdvanced = this.serializeAdvancedFilters(advancedFilters)
        this.onFiltersChange.emit(this.buildFiltersChange(selectedValues, advancedFilters))
        this.onSelectedValuesChange.emit(serialized)
        this.updateQueryParams(serialized, serializedAdvanced)
      }, 2000)
    })

    this.destroyRef.onDestroy(() => {
      if (debounceTimer) clearTimeout(debounceTimer)
    })
  }

  protected updateFilterValue(key: string, value: FilterValue): void {
    this.selectedValues.update(values => {
      const updated = { ...values, [key]: value }
      if (value !== null) {
        const filter = this.mergedFilters().find(f => filterKey(f) === key)
        if (filter) {
          this.mergedFilters()
            .filter(f => filterKey(f) !== key && f.field.id === filter.field.id)
            .forEach(f => {
              updated[filterKey(f)] = null
            })
        }
      }
      return updated
    })
  }

  protected toggleFilterValue(key: string, value: string): void {
    const current = this.selectedValues()[key]
    this.updateFilterValue(key, current === value ? null : value)
  }

  protected dateValues = computed(() => {
    const values = this.selectedValues()
    return Object.fromEntries(
      this.mergedFilters()
        .filter(f => f.type === 'date')
        .map(f => {
          const val = values[filterKey(f)]
          if (isDateRangeValue(val)) {
            return [filterKey(f), `${this.isoToDisplay(val.start)} - ${this.isoToDisplay(val.end)}`]
          }
          const str = val as string | null
          let formatted = str || ''
          if (str) {
            const d = dayjs(str, ['YYYY-MM-DD', 'DD/MM/YYYY'], true)
            if (d.isValid()) formatted = d.format('DD/MM/YYYY')
            else {
              const dFallback = dayjs(str)
              if (dFallback.isValid()) formatted = dFallback.format('DD/MM/YYYY')
            }
          }
          return [filterKey(f), formatted]
        })
    )
  })

  protected onDateChange(
    filter: KanbanDateFilter,
    event: { date?: string; start?: string; end?: string }
  ): void {
    if (event.start && event.end) {
      if (event.start === event.end) {
        this.updateFilterValue(filterKey(filter), event.start)
      } else {
        this.updateFilterValue(filterKey(filter), { start: event.start, end: event.end })
      }
    }
  }

  protected onTextChange(filter: KanbanTextFilter, value: string): void {
    this.updateFilterValue(filterKey(filter), value || null)
  }

  protected toggleAdvancedPanel(): void {
    const willShow = !this.showAdvancedPanel()
    if (willShow) {
      const el = this.advancedBtnRef()?.nativeElement
      if (el) {
        const rect = el.getBoundingClientRect()
        this.panelPosition.set({
          top: rect.bottom + 8,
          left: rect.left
        })
      }
    }
    this.showAdvancedPanel.set(willShow)
  }

  protected onAdvancedApply(filters: AdvancedFilters): void {
    this.advancedFilters.set(filters)
    this.showAdvancedPanel.set(false)
  }

  protected phaseOptions = computed(() => this.phases().map(p => ({ value: p.id, text: p.name })))

  protected multiselectOptions = computed(() => {
    const phases = this.phaseOptions()
    return new Map(
      this.mergedFilters()
        .filter((f): f is typeof f & { type: 'multiselect' } => f.type === 'multiselect')
        .map(f => [
          filterKey(f),
          f.field.id === 'phaseId'
            ? phases
            : f.options.map(o => ({ value: o.value, text: o.label }))
        ])
    )
  })

  protected multiselectSelectedValues = computed(() => {
    const values = this.selectedValues()
    const optionsMap = this.multiselectOptions()
    return new Map(
      this.mergedFilters()
        .filter((f): f is typeof f & { type: 'multiselect' } => f.type === 'multiselect')
        .map(f => {
          const key = filterKey(f)
          const selected = values[key]
          const options = optionsMap.get(key) ?? []
          const selectedOptions = Array.isArray(selected)
            ? options.filter(o => selected.includes(o.value))
            : []
          return [key, selectedOptions]
        })
    )
  })

  protected onMultiSelectChange(key: string, selected: { value: string; text: string }[]): void {
    const values = selected.map(o => o.value)
    this.updateFilterValue(key, values.length ? values : null)
  }

  protected onAdvancedClear(fieldId: string): void {
    this.advancedFilters.update(filters => {
      const { [fieldId]: _, ...rest } = filters
      return rest
    })
  }

  private resolveInitialValues(filters: KanbanFilter[]): Record<string, FilterValue> {
    return Object.fromEntries(filters.map(f => [filterKey(f), resolveInitialValue(f)]))
  }

  private applyPreSelectedValues(
    initial: Record<string, FilterValue>,
    preSelected: Record<string, string | string[]>
  ): void {
    const filterByKey = new Map(this.mergedFilters().map(f => [filterKey(f), f]))
    Object.entries(preSelected)
      .filter(([key, value]) => value !== undefined && value !== '' && key in initial)
      .forEach(([key, raw]) => {
        const parsed = parseStandardQueryParam(raw, filterByKey.get(key)?.type)
        if (parsed !== null) initial[key] = parsed
      })
  }

  private enforceMutualExclusivity(
    initial: Record<string, FilterValue>,
    filters: KanbanFilter[],
    preSelected: Record<string, string | string[]>
  ): void {
    const fieldGroups = filters.reduce((groups, f) => {
      const id = f.field.id
      groups.set(id, [...(groups.get(id) || []), filterKey(f)])
      return groups
    }, new Map<string, string[]>())

    fieldGroups.forEach(group => {
      if (group.length <= 1) return
      const activeKeys = group.filter(k => initial[k] !== null && initial[k] !== undefined)
      if (activeKeys.length > 1) {
        const preSelectedKey = activeKeys.find(k => k in preSelected)
        const winner = preSelectedKey ?? activeKeys[activeKeys.length - 1]
        activeKeys
          .filter(k => k !== winner)
          .forEach(k => {
            initial[k] = null
          })
      }
    })
  }

  private buildFiltersChange(
    selectedValues: Record<string, FilterValue>,
    advancedFilters: AdvancedFilters
  ): KanbanFiltersChange {
    const standardFilters = Object.entries(selectedValues).flatMap(([key, value]) => {
      if (value === null) return []
      const filter = this.mergedFilters().find(f => filterKey(f) === key)
      if (!filter) return []
      return buildFilterEntries(filter, value, this.multiselectOptions().get(key))
    })

    const advancedConfigs = Object.entries(advancedFilters)
      .filter(([, { value }]) => !!value)
      .flatMap(([fieldId, { operator, value, rangeEnd }]) => {
        const field = FIXED_CARD_FIELD_IDS.has(fieldId as Kanban.Card.Filters.Fixed['id'])
          ? { fixed: true as const, id: fieldId as Kanban.Card.Filters.Fixed['id'] }
          : { fixed: false as const, id: fieldId }
        if (rangeEnd) {
          return [
            { field, operator: 'gte' as Operator, value },
            { field, operator: 'lte' as Operator, value: rangeEnd }
          ]
        }
        return [{ field, operator, value }]
      })

    return [...standardFilters, ...advancedConfigs]
  }

  private serializeSelectedValues(
    selectedValues: Record<string, FilterValue>
  ): Record<string, string> {
    const result: Record<string, string> = {}
    for (const [key, value] of Object.entries(selectedValues)) {
      if (value === null) continue
      const filter = this.mergedFilters().find(f => filterKey(f) === key)
      if (!filter) continue
      const serialized = serializeFilterValue(filter, value)
      if (serialized) result[key] = serialized
    }
    return result
  }

  private readQueryParams(): Record<string, string | string[]> {
    const urlTree = this.router.parseUrl(this.location.path(true) || '/')
    return urlTree.queryParams as Record<string, string | string[]>
  }

  private updateQueryParams(
    values: Record<string, string>,
    advancedValues: Record<string, string>
  ): void {
    const urlTree = this.router.parseUrl(this.location.path(true) || '/')
    const queryParams = { ...urlTree.queryParams } as Record<string, string | string[] | null>

    this.mergedFilters().forEach(f => {
      queryParams[filterKey(f)] = null
    })
    this.boardFields().forEach(f => {
      queryParams[f.id] = null
    })

    const setParam = (key: string, value: string) => {
      if (value.startsWith('range.')) {
        const combined = value.slice(6)
        const sepIdx = combined.indexOf('_')
        if (sepIdx !== -1) {
          queryParams[key] = [
            `gte.${combined.slice(0, sepIdx)}`,
            `lte.${combined.slice(sepIdx + 1)}`
          ]
          return
        }
      }
      queryParams[key] = value
    }

    Object.entries(advancedValues).forEach(([key, value]) => {
      if (value !== null && value !== '') setParam(key, value)
    })
    Object.entries(values).forEach(([key, value]) => {
      if (value !== null && value !== '') setParam(key, value)
    })

    Object.keys(queryParams).forEach(k => {
      if (queryParams[k] === null) delete queryParams[k]
    })

    urlTree.queryParams = queryParams
    this.location.replaceState(this.router.serializeUrl(urlTree))
  }

  private serializeAdvancedFilters(advancedFilters: AdvancedFilters): Record<string, string> {
    return Object.entries(advancedFilters)
      .filter(([, { value }]) => !!value)
      .reduce(
        (acc, [fieldId, { operator, value, rangeEnd }]) => {
          acc[fieldId] = rangeEnd ? `range.${value}_${rangeEnd}` : `${operator}.${value}`
          return acc
        },
        {} as Record<string, string>
      )
  }

  private readAdvancedFiltersFromQueryParams(
    queryParams: Record<string, string | string[]>
  ): AdvancedFilters {
    const regularKeys = new Set(this.mergedFilters().map(f => filterKey(f)))
    const result: AdvancedFilters = {}
    Object.entries(queryParams).forEach(([key, raw]) => {
      if (regularKeys.has(key)) return
      const parsed = parseAdvancedQueryParam(raw)
      if (parsed) result[key] = parsed
    })
    return result
  }

  private isoToDisplay(iso: string): string {
    const d = dayjs(iso, 'YYYY-MM-DD', true)
    return d.isValid() ? d.format('DD/MM/YYYY') : iso
  }
}
